import React from "react";
import PropTypes from "prop-types";
import { makeStyles, Box } from "@material-ui/core";
import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import PriceRoutePnl from "../../../../../components/Pnl/PriceRoutePnl.js";
import PnlSkeleton from "../../../../../components/Pnl/PnlSkeleton.js";
import { ThreeDots } from "react-loader-spinner";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { useTranslation } from "react-i18next";
import { ScenarioSelector } from "../../../sharedComponents/ScenarioSelector";

const priceRoutePnLDisplayStyle = (theme, clientColor) => ({
  pnlContainer: {
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    "@media (min-width: 960px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    overflow: "auto",
    flex: 1,
  },
  pnlCard: {
    minHeight: "400px",
    height: "99%",
    maxHeight: "99%",
    position: "relative",
    marginTop: 0,
    display: "flex",
    flexDirection: "column",
  },
  pnlCardBody: {
    padding: 0,
    position: "relative",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  scenarioSelectorContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    zIndex: 3,
    backgroundColor: "rgba(255, 255, 255, 0.8)", // Match source transparency
    padding: "12px 16px", // Match source padding (converted from 6px 12px scaled)
    borderRadius: "4px", // Match source border radius
    width: "90%",
    maxWidth: 280,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)", // Match source shadow
  },
  selectorLabel: {
    color: "#888", // Match source color
    fontSize: "14px", // Match source
    fontWeight: "500", // Match source
    marginBottom: theme.spacing(1.5),
  },
  selectFormControl: {
    width: "100%",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: "#999 !important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: "#666 !important", // Neutral color instead of primary
      },
    },
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    zIndex: 2,
  },
  errorState: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    zIndex: 2,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    width: "90%",
    maxWidth: 280,
  },
});

PriceRoutePnLDisplay.propTypes = {
  selectedScenarios: PropTypes.object.isRequired,
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  isScenariosLoading: PropTypes.bool,
  pnlData: PropTypes.object.isRequired,
  loadingStates: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  drillDownsOpen: PropTypes.object.isRequired,
  clientColor: PropTypes.string.isRequired,
  onScenarioSelect: PropTypes.func.isRequired,
  createSetDrillDownsOpenForSlot: PropTypes.func.isRequired,
  loadPnlData: PropTypes.func.isRequired,
  hoveredRowIndex: PropTypes.number,
  setHoveredRowIndex: PropTypes.func.isRequired,
  routeFilterOptions: PropTypes.object,
  routeFilters: PropTypes.object,
  createApplyRouteFiltersForSlot: PropTypes.func,
  createClearRouteFiltersForSlot: PropTypes.func,
};

export default function PriceRoutePnLDisplay({
  selectedScenarios,
  scenarios,
  folders = [],
  isScenariosLoading = false,
  pnlData,
  loadingStates,
  errors,
  drillDownsOpen,
  clientColor,
  onScenarioSelect,
  createSetDrillDownsOpenForSlot,
  loadPnlData,
  hoveredRowIndex,
  setHoveredRowIndex,
  routeFilterOptions,
  routeFilters,
  createApplyRouteFiltersForSlot,
  createClearRouteFiltersForSlot,
}) {
  const useStyles = makeStyles((theme) =>
    priceRoutePnLDisplayStyle(theme, clientColor),
  );
  const classes = useStyles();
  const { t } = useTranslation("translation", { keyPrefix: "rgm.cockpit" });

  const handleRemoveScenario = (slotIndex) => {
    onScenarioSelect(slotIndex, null);
  };

  return (
    <Box className={classes.pnlContainer}>
      {[0, 1].map((slotIndex) => {
        const scenario = selectedScenarios[slotIndex];
        const hasData = scenario && pnlData[slotIndex];
        const hasError = scenario && errors[slotIndex];
        const isLoading = scenario && loadingStates[slotIndex];
        const showSelector = !scenario || (!hasData && !isLoading && !hasError);

        return (
          <Card key={slotIndex} className={classes.pnlCard}>
            <CardBody className={classes.pnlCardBody}>
              {/* Base layer: Show skeleton when no data */}
              {(!scenario || (!hasData && !hasError)) && <PnlSkeleton />}

              {/* Content layer: Show actual PnL when data is available */}
              {hasData &&
                !hasError &&
                (() => {
                  const slotPnlData = pnlData[slotIndex];

                  return (
                    <PriceRoutePnl
                      plStructure={slotPnlData.plStructure}
                      plName={slotPnlData.plName || scenario.scenario_name}
                      columnGroups={slotPnlData.columnGroups}
                      drillDownsOpen={
                        Array.isArray(drillDownsOpen[slotIndex])
                          ? drillDownsOpen[slotIndex]
                          : []
                      }
                      setDrillDownsOpen={createSetDrillDownsOpenForSlot(
                        slotIndex,
                      )}
                      timersAllowed={true}
                      removePnl={() => handleRemoveScenario(slotIndex)}
                      isLoading={false}
                      hoveredRowIndex={hoveredRowIndex}
                      setHoveredRowIndex={setHoveredRowIndex}
                      routeFilterOptions={routeFilterOptions}
                      routeFilters={routeFilters[slotIndex] || {}}
                      onApplyRouteFilters={createApplyRouteFiltersForSlot(
                        slotIndex,
                      )}
                      onClearRouteFilters={createClearRouteFiltersForSlot(
                        slotIndex,
                      )}
                      availableScenarios={scenarios}
                      currentScenario={scenario}
                      onScenarioChange={(newScenario) =>
                        onScenarioSelect(slotIndex, newScenario)
                      }
                    />
                  );
                })()}

              {/* Scenario selector overlay - only show when no scenario selected or no data */}
              {showSelector && (
                <Box className={classes.scenarioSelectorContainer}>
                  <Typography className={classes.selectorLabel}>
                    {t("map.tooltip.select_scenario")}
                  </Typography>
                  <ScenarioSelector
                    scenarios={scenarios}
                    folders={folders}
                    value={scenario ? scenario.scenario_id : null}
                    onChange={(scenarioId) => {
                      const newScenario =
                        scenarios.find((s) => s.scenario_id === scenarioId) ||
                        null;
                      onScenarioSelect(slotIndex, newScenario);
                    }}
                    placeholder={t("overview.choose_scenario")}
                    clientColor={clientColor}
                    isLoading={isScenariosLoading}
                    fullWidth
                  />
                </Box>
              )}

              {/* Error layer */}
              {hasError && (
                <Box className={classes.errorState}>
                  <Typography color="error" variant="body2">
                    {errors[slotIndex]}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => loadPnlData(slotIndex)}
                    color="primary"
                  >
                    {t("common.retry")}
                  </Button>
                </Box>
              )}

              {/* Loading layer */}
              {isLoading && (
                <Box className={classes.loadingOverlay}>
                  <ThreeDots
                    height="60"
                    width="60"
                    radius="9"
                    color={clientColor}
                    ariaLabel="three-dots-loading"
                    visible={true}
                  />
                </Box>
              )}
            </CardBody>
          </Card>
        );
      })}
    </Box>
  );
}
